import { BsSearch } from "react-icons/bs";
import ClientsList from "../ClientsList";
import { FaHashtag } from "react-icons/fa";
import { MdOutlineWeb } from "react-icons/md";
import React from "react";
import { RiQuillPenLine } from "react-icons/ri";
import { TbAlignBoxLeftMiddle } from "react-icons/tb";
import { siteConfig } from "@/app/_config/siteConfig";

const services = [
  {
    title: "Research",
    icon: <BsSearch className="text-sky-500" />,
    description:
      "Primary sources, document trails, expert interviews. We do the reading so the writing means something — whether the output is six thousand words or six seconds. When the work calls for it, we capture the process with Stone, so readers can see what we saw.",
  },
  {
    title: "Editorial",
    icon: <RiQuillPenLine className="text-orange-500" />,
    description:
      "Structure, argument, line edits. Getting a piece to say what it actually means, in the voice it should be in. Equally at home with a long investigation, a punchy op-ed, or a campaign email that has to land in three seconds.",
  },
  {
    title: "Content",
    icon: <TbAlignBoxLeftMiddle className="text-violet-500" />,
    description:
      "Long-form, short-form, multi-format. Built to be read, watched, and shared.",
  },
  {
    title: "Social media",
    icon: <FaHashtag className="text-indigo-500" />,
    description:
      "Strategy, production, and channel management across Instagram, TikTok, Threads, Facebook, YouTube and X. Our founder's personal channels — political commentary, vox pops, current affairs — have pulled over 10 million views in a single month, organically. No boosting, no buying. We know what travels and why.",
  },
  {
    title: "Web design",
    icon: <MdOutlineWeb className="text-emerald-500" />,
    description:
      "Sites and pages that match the seriousness of the work. Fast, accessible, no dark patterns. Stone-ready when transparency matters.",
  },
];

export default function HomepageAboutSection() {
  return (
    <section className="relative w-full overflow-hidden">
      {/* Gradient blobs — mirrored from hero */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% -5%, hsl(249 53% 55% / 0.09) 0%, transparent 58%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 90% 110%, hsl(84 70% 55% / 0.07) 0%, transparent 52%)",
        }}
      />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-14 px-4 py-16 sm:px-6 md:py-20 lg:px-8">
        <div>
          <h2 className="font-PlayfairDisplay text-4xl font-semibold md:text-5xl">
            What we do
          </h2>
          <div className="mt-6 divide-y divide-foreground/10 border-y border-foreground/10 lg:grid lg:grid-cols-2 lg:gap-12 lg:divide-y-0 lg:border-y-0">
            {services.map((service) => (
              <article
                key={service.title}
                className="py-7 lg:border-b lg:border-foreground/10 md:py-8"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{service.icon}</span>
                  <h3 className="font-Cabin text-2xl font-semibold leading-tight md:text-3xl">
                    {service.title}
                  </h3>
                </div>
                <p className="mt-3 max-w-5xl font-Cabin text-lg leading-8 text-foreground/80 dark:text-foreground/85 md:text-xl md:leading-9">
                  {service.description}
                </p>
              </article>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-PlayfairDisplay md:py-12 text-4xl text-center md:text-left font-semibold md:text-5xl">
            Recent clients
          </h2>
          <div className="mt-6">
            <ClientsList />
          </div>
        </div>

        <div className="border-t border-foreground/10 pt-10">
          <h2 className="font-PlayfairDisplay text-4xl font-semibold md:text-5xl">
            Who this is for
          </h2>
          <p className="mt-4 max-w-5xl font-Cabin text-lg leading-8 text-foreground/80 dark:text-foreground/85 md:text-xl md:leading-9">
            Anyone making digital non-fiction who wants it to be trusted, found,
            and remembered. <br /> Newsrooms, NGOs, academics, campaigners,
            independent researchers.
          </p>
        </div>

        <div className="border-t border-foreground/10 pt-10">
          <h2 className="font-PlayfairDisplay text-4xl font-semibold md:text-5xl">
            How to work with us
          </h2>
          <p className="mt-4 max-w-5xl font-Cabin text-lg leading-8 text-foreground/80 dark:text-foreground/85 md:text-xl md:leading-9">
            Email{" "}
            <a
              className="mr-1 dark:text-purple-400 text-purple-700"
              href={"mailto:austin@writeinstone.com"}
            >
              austin@writeinstone.com
            </a>
            with what you&apos;re working on.
            <br />
            We&apos;ll tell you straight whether we can help.
          </p>
        </div>

        <div className="py-24">
          <h2 className="mt-4 max-w-3xl font-PlayfairDisplay text-5xl font-semibold leading-none text-foreground md:text-6xl lg:text-7xl">
            {siteConfig.tagLine}
          </h2>
        </div>
      </div>
    </section>
  );
}
